/**
 * Parent Chat: register FCM web push and persist tokens under parents/{uid}/private/deviceTokens.
 */
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { app, db } from "./firebase";

const SW_PATH = "/firebase-messaging-sw.js";
const MAX_STORED_TOKENS = 8;

function getVapidKey() {
  const k = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  return typeof k === "string" && k.trim() ? k.trim() : "";
}

/**
 * @returns {Promise<{ ok: boolean, reason?: string, token?: string }>}
 */
export async function registerParentChatFcm(uid) {
  if (!uid) return { ok: false, reason: "no_uid" };
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return { ok: false, reason: "unsupported" };
  }
  const supported = await isSupported().catch(() => false);
  if (!supported) return { ok: false, reason: "unsupported" };

  const vapidKey = getVapidKey();
  if (!vapidKey) {
    console.warn("[FCM] Set REACT_APP_FIREBASE_VAPID_KEY (Web Push certificate in Firebase Console).");
    return { ok: false, reason: "no_vapid" };
  }

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    return { ok: false, reason: permission === "denied" ? "denied" : "dismissed" };
  }

  const messaging = getMessaging(app);
  const registration = await navigator.serviceWorker.register(SW_PATH);
  await navigator.serviceWorker.ready;

  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });

  if (!token) return { ok: false, reason: "no_token" };

  const ref = doc(db, "parents", uid, "private", "deviceTokens");
  const snap = await getDoc(ref);
  const prev = snap.exists() && Array.isArray(snap.data().tokens) ? snap.data().tokens : [];
  const merged = [...new Set([token, ...prev.filter((t) => typeof t === "string")])].slice(
    0,
    MAX_STORED_TOKENS
  );
  await setDoc(
    ref,
    {
      tokens: merged,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { ok: true, token };
}

/**
 * Subscribe to foreground FCM (optional toast in caller).
 * @returns {Promise<() => void>} unsubscribe function
 */
export async function subscribeForegroundChatMessages(onPayload) {
  if (typeof window === "undefined") return () => {};
  const supported = await isSupported().catch(() => false);
  if (!supported) return () => {};
  const messaging = getMessaging(app);
  return onMessage(messaging, typeof onPayload === "function" ? onPayload : () => {});
}
